import React, { useEffect, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import { BOOK } from '../data/mock';
import Reveal from './Reveal';

const scrollToWaitlist = () => {
  const el = document.getElementById('waitlist');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const SoldOutNotice = () => (
  <div data-testid="tickets-sold-out-notice" className="text-center py-8 md:py-12">
    <p className="font-fell-sc text-[11px] tracking-[0.4em] text-[#7a4a20] uppercase">
      Halloween 2026
    </p>
    <p className="mt-5 font-display text-5xl md:text-6xl text-[#2a1a10] lowercase leading-none">
      sold out
    </p>
    <p className="mt-6 font-fell italic text-base md:text-lg text-[#4a3220] leading-relaxed max-w-md mx-auto">
      {BOOK.soldOutNote}
    </p>
    <button
      type="button"
      onClick={scrollToWaitlist}
      data-testid="tickets-mailing-list-button"
      className="group relative mt-8 inline-flex items-center gap-3 px-10 py-4 border border-[#8b5a2a]/70 bg-[#2a1a10] hover:bg-[#3a2416] text-[#f0e2c1] transition-all duration-500 font-fell-sc text-[11px] tracking-[0.4em] uppercase"
    >
      <Mail className="w-4 h-4 text-[#d9a55e]" />
      <span className="relative z-10">Sign up on the mailing list</span>
      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-[#c8935a]/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
    </button>
  </div>
);

const TicketWidget = () => {
  const widgetRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!widgetRef.current) return;
    widgetRef.current.innerHTML = '';

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src =
      'https://widgets.designmynight.com/tonic/ticket-widget-v2.min.js?item-ids=6ab259a747116a3ed064176c&showWaitList=false';
    script.setAttribute('data-dmn-ticket-widget', 'true');
    script.async = true;
    script.onload = () => setLoaded(true);
    script.onerror = () => setError(true);
    widgetRef.current.appendChild(script);

    const t = setTimeout(() => setLoaded(true), 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <div
        ref={widgetRef}
        className="tonic-widget-container min-h-[320px] flex items-center justify-center"
      />
      {!loaded && !error && (
        <div className="text-center py-10">
          <div className="inline-flex items-center gap-3 text-[#7a4a20] font-fell italic text-sm">
            <span className="w-2 h-2 rounded-full bg-[#c8935a] flicker" />
            Summoning the Court Record…
          </div>
        </div>
      )}
      {error && (
        <div className="text-center py-10 font-fell italic text-[#7a4a20]">
          The Court Record is temporarily unavailable. Please refresh, or
          email producer@colabtheatre.co.uk to reserve.
        </div>
      )}
    </>
  );
};

const BookSection = React.forwardRef((props, ref) => {
  return (
    <section
      id="book"
      ref={ref}
      className="relative py-24 md:py-40 px-6 md:px-10 bg-gradient-to-b from-[#0a0705] via-[#120a06] to-[#0a0705]"
    >
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <p className="text-[10px] tracking-[0.5em] text-[#c8935a] font-fell-sc">
            THIS HALLOWEEN
          </p>
        </Reveal>

        <Reveal delay={100}>
          <h2 className="mt-6 font-display text-6xl md:text-7xl text-[#e8dcc0] lowercase">
            {BOOK.heading}
          </h2>
        </Reveal>

        <Reveal delay={200}>
          <div className="mt-8 divider-ornament max-w-md mx-auto">
            <span className="text-[9px] tracking-[0.4em] font-fell-sc">
              {BOOK.soldOut ? 'SOLD OUT' : 'CHOOSE YOUR NIGHT'}
            </span>
          </div>
        </Reveal>

        <Reveal delay={300}>
          <p className="mt-8 font-fell italic text-lg md:text-xl text-[#d9c8a3] max-w-xl mx-auto leading-relaxed">
            {BOOK.subheading}
          </p>
        </Reveal>
      </div>

      <Reveal delay={400}>
        <div className="relative max-w-3xl mx-auto mt-14">
          <div
            className="absolute -inset-8 pointer-events-none opacity-40 flicker"
            style={{
              background:
                'radial-gradient(circle at 50% 20%, rgba(220,150,70,0.25) 0%, transparent 60%)'
            }}
          />

          <div className="relative bg-[#f0e2c1] text-[#2a1a10] border border-[#c8935a]/60 shadow-[0_20px_80px_-20px_rgba(200,140,60,0.35)] p-6 md:p-10">
            <div className="absolute top-2 left-2 w-8 h-8 border-l border-t border-[#8b5a2a]/50" />
            <div className="absolute top-2 right-2 w-8 h-8 border-r border-t border-[#8b5a2a]/50" />
            <div className="absolute bottom-2 left-2 w-8 h-8 border-l border-b border-[#8b5a2a]/50" />
            <div className="absolute bottom-2 right-2 w-8 h-8 border-r border-b border-[#8b5a2a]/50" />

            <p className="font-fell-sc text-center text-[11px] tracking-[0.4em] text-[#7a4a20] uppercase mb-6">
              — Court Record of Sittings —
            </p>

            {BOOK.soldOut ? (
              <>
                <SoldOutNotice />
                <div className="my-6 flex items-center gap-4">
                  <span className="h-px flex-1 bg-[#8b5a2a]/40" />
                  <span className="font-fell-sc text-[10px] tracking-[0.4em] text-[#7a4a20] uppercase whitespace-nowrap">
                    or check DesignMyNight for returns
                  </span>
                  <span className="h-px flex-1 bg-[#8b5a2a]/40" />
                </div>
                <TicketWidget />
              </>
            ) : (
              <TicketWidget />
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
});

export default BookSection;
